import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider } from "@/contexts/AuthContext";
import { SubscriptionProvider } from "@/contexts/SubscriptionContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { lazy, Suspense } from "react";
import { CookieConsent } from "@/components/CookieConsent";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { PWAInstallTracker } from "@/components/pwa/PWAInstallTracker";
import Index from "./pages/Index";
import PublicBio from "./pages/PublicBio";
import PublicContact from "./pages/PublicContact";
import PublicStore from "./pages/PublicStore";
import PublicChat from "./pages/PublicChat";
const Auth = lazy(() => import("./pages/Auth"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Editor = lazy(() => import("./pages/Editor"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Contact = lazy(() => import("./pages/Contact"));
const Blog = lazy(() => import("./pages/Blog"));
const BlogPost = lazy(() => import("./pages/BlogPost"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AffiliateProgramTerms = lazy(() => import("./pages/AffiliateProgramTerms"));
const AffiliatePaymentsPolicy = lazy(() => import("./pages/AffiliatePaymentsPolicy"));
const AffiliateCompliancePolicy = lazy(() => import("./pages/AffiliateCompliancePolicy"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminPromos = lazy(() => import("./pages/admin/AdminPromos"));
const AdminAffiliates = lazy(() => import("./pages/admin/AdminAffiliates"));
const AdminNotifications = lazy(() => import("./pages/admin/AdminNotifications"));
const AdminAffiliateCommissions = lazy(() => import("./pages/admin/AdminAffiliateCommissions"));
const AdminQlivo = lazy(() => import("./pages/admin/AdminQlivo"));
const AdminAssets = lazy(() => import("./pages/admin/AdminAssets"));const AdminEmailTemplates = lazy(() => import('./pages/admin/AdminEmailTemplates'));const AiAgent = lazy(() => import("./pages/AiAgent"));
const DevToolsWidget = import.meta.env.DEV
  ? lazy(() => import("./components/dev/DevToolsWidget"))
  : null;
const AiGrowthPlatform = lazy(() => import("./pages/AiGrowthPlatform"));
const AiAnalytics = lazy(() => import("./pages/AiAnalytics"));
const AiLeadAutomation = lazy(() => import("./pages/AiLeadAutomation"));
const ThemePreviewGenerator = lazy(() => import("./pages/ThemePreviewGenerator"));
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// ── Bio-cluster route animator ────────────────────────────────────────────────
// Wraps all routes in AnimatePresence so public pages can smoothly cross-fade.
// key={location.pathname} ensures a clean remount on every pathname change.
function AppRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait" initial={false}>
      <Routes key={location.pathname}>
        <Route path="/" element={<Index />} />
        <Route path="/pricing" element={<Index />} />
        <Route path="/analytics" element={<Index />} />
        <Route path="/mini-store" element={<Index />} />
        <Route path="/custom-domain" element={<Index />} />
        <Route path="/for-creators" element={<Index />} />
        <Route path="/linktree-alternative" element={<Index />} />
        <Route path="/ai-growth-platform" element={<AiGrowthPlatform />} />
        <Route path="/ai-agent" element={<AiAgent />} />
        <Route path="/ai-analytics" element={<AiAnalytics />} />
        <Route path="/ai-lead-automation" element={<AiLeadAutomation />} />
        <Route path="/auth" element={<Auth />} />
        <Route path="/dashboard" element={<ErrorBoundary label="Dashboard"><Dashboard /></ErrorBoundary>} />
        <Route path="/editor" element={<Editor />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/cookies" element={<CookiePolicy />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        <Route path="/affiliate/terms" element={<AffiliateProgramTerms />} />
        <Route path="/affiliate/payments-policy" element={<AffiliatePaymentsPolicy />} />
        <Route path="/affiliate/compliance-policy" element={<AffiliateCompliancePolicy />} />
        <Route path="/chat/:agentId" element={<PublicChat />} />
        <Route path="/:username/store" element={<PublicStore />} />
        <Route path="/:username/contact" element={<PublicContact />} />
        <Route path="/:username" element={<PublicBio />} />
        {/* Admin routes */}
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/promos" element={<AdminPromos />} />
        <Route path="/admin/notifications" element={<AdminNotifications />} />
        <Route path="/admin/affiliates" element={<AdminAffiliates />} />
        <Route path="/admin/affiliate-commissions" element={<AdminAffiliateCommissions />} />
        <Route path="/admin/qlivo" element={<AdminQlivo />} />
        <Route path="/admin/assets" element={<AdminAssets />} />
        <Route path="/admin/email-templates" element={<AdminEmailTemplates />} />
        {/* dev-only: generate static theme preview screenshots */}
        {import.meta.env.DEV && <Route path="/theme-preview-generator" element={<ThemePreviewGenerator />} />}
        {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
        <Route path="*" element={<NotFound />} />
      </Routes>
    </AnimatePresence>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <LanguageProvider>
        <AuthProvider>
          <SubscriptionProvider>
            <div className="relative min-h-screen">
              <div className="relative z-10">
                <Toaster />
                <Sonner />
                <PWAInstallTracker />
                {DevToolsWidget && <Suspense fallback={null}><DevToolsWidget /></Suspense>}
                <BrowserRouter>
                  <ScrollToTop />
                  <ErrorBoundary label="aplicação">
                  <Suspense fallback={null}>
                    <AppRoutes />
                  </Suspense>
                  <CookieConsent />                  </ErrorBoundary>                </BrowserRouter>
              </div>
            </div>
          </SubscriptionProvider>
        </AuthProvider>
      </LanguageProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
